export type FilterTab = 'nl2sql' | 'filter' | 'sql'

export interface FilterTabFlags {
    nl2sqlTab: boolean
    filterTab: boolean
    sqlTab: boolean
}

// What the page shows if configuration would leave no tab at all.
export const FALLBACK_TABS: FilterTab[] = ['filter', 'sql']

const TAB_ORDER: FilterTab[] = ['nl2sql', 'filter', 'sql']

/**
 * Tabs to render, left to right.
 * - `requested` (set by a host that renders this page itself, e.g. the chat agent) wins over the product flags
 *   for the existing tabs, so hiding them on the product page never affects such a host.
 * - The NL2SQL tab always needs its own master flag.
 * - `nl2sqlBlocked` (pre-made `user_design` experiments, data kinds the agent does not cover) removes the NL2SQL tab
 *   so those pages keep today's behavior.
 */
export const resolveVisibleTabs = (flags: FilterTabFlags, requested?: FilterTab[], nl2sqlBlocked = false): FilterTab[] => {
    const enabledByFlags: Record<FilterTab, boolean> = { nl2sql: flags.nl2sqlTab, filter: flags.filterTab, sql: flags.sqlTab }
    const tabs = TAB_ORDER.filter((tab) => (requested ? requested.includes(tab) : enabledByFlags[tab]))
        .filter((tab) => tab !== 'nl2sql' || flags.nl2sqlTab)
        .filter((tab) => tab !== 'nl2sql' || !nl2sqlBlocked)
    return tabs.length > 0 ? tabs : FALLBACK_TABS
}

const hasSavedFilters = (filters: any): boolean => !!filters && typeof filters === 'object' && Object.keys(filters).length > 0

/**
 * Tab to open first. Saved work wins over the landing tab so existing experiments reopen where they were:
 * saved SQL -> SQL tab, saved filters -> filter tab, otherwise the first visible tab.
 * A saved tab that is hidden falls back to the first visible tab (the page then restores the saved query itself).
 */
export const resolveInitialTab = (experiment: any, visibleTabs: FilterTab[], filterKey = 'design_table_filters'): FilterTab => {
    const saved = experiment?.experiment_metadatas?.[filterKey]
    if ((saved?.sql_query || saved?.sql) && visibleTabs.includes('sql')) return 'sql'
    if (hasSavedFilters(saved?.filters) && visibleTabs.includes('filter')) return 'filter'
    return visibleTabs[0]
}

/**
 * Tab used for the first render. When the NL2SQL tab is off this is always "Add Filters" (or the first visible tab if
 * that one is hidden), which is how the page behaved before the tab existed; saved SQL is then switched to by the page.
 */
export const resolveStartTab = (experiment: any, visibleTabs: FilterTab[], filterKey = 'design_table_filters'): FilterTab => {
    if (visibleTabs.includes('nl2sql')) return resolveInitialTab(experiment, visibleTabs, filterKey)
    return visibleTabs.includes('filter') ? 'filter' : visibleTabs[0]
}

interface SaveQueryPromptOptions {
    currentQueryExecuted: boolean
    dialog: { confirm: (options: any) => void }
    t: (key: string) => string
    sqlRef: { current?: { saveFeatures: () => void } | null }
    proceed: () => void
}

// Same prompt the Add Filters tab shows before leaving an executed SQL query.
export const proceedOrPromptToSaveQuery = ({ currentQueryExecuted, dialog, t, sqlRef, proceed }: SaveQueryPromptOptions) => {
    if (!currentQueryExecuted) {
        proceed()
        return
    }
    dialog.confirm({
        type: 'delete',
        title: t('Save this query for future use?'),
        message: t('Would you like to save this query as a reusable Feature for future experiments?'),
        cancelButtonLabel: t('No, Not Now'),
        confirmButtonLabel: t('Yes, Save Query'),
        onConfirm: () => sqlRef?.current?.saveFeatures(),
        onCancel: proceed,
    })
}
